import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InsightsIcon from '@mui/icons-material/Insights';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useDataFetch } from '../../core/hooks/useDataFetch';
import { useSocket } from '../../core/hooks/useSocket';
import useCookie from '../../core/hooks/useCookie';
import { useConfig } from '../../core/store/selectors/useConfig';
import { Config } from '../../config/config';
import { fmtDate, fmtMonthYear, groupByTrend, statsOf } from './logic';
import FilterBar from './components/FilterBar';
import SummaryHeader from './components/SummaryHeader';
import TrendPanel from './components/TrendPanel';
import NeutralStrip from './components/NeutralStrip';
import { GrowthPill } from './components/BucketCard';
import FishingCard from './components/FishingCard';
import RefreshCountdown from './components/RefreshCountdown';

/**
 * Screening - same as IDash4You.Partner.UI src/pages/Screening.tsx (Canada), for Angel / IN.Core:
 * Analyze (trend buckets with each symbol's current quote and backtest win %) and Live Screening (a
 * backend job refreshes live scores every scanInterval and pushes them over the WebSocket, plus the
 * Fishing bucket). India has no currency / category, so no filters and no category summary.
 */
const LIVE_STAGING = 'liveScreening';
// The market check is the buy engine's (live order book of a few test tokens) - an Angel stream
// connection of its own, so it runs once when the page opens (reload the page to re-check). Live
// Screening checks once more itself when it starts.

const UNKNOWN_MARKET = { open: false, label: 'Checking market…' };

export default function Screening() {
  const { callAPI } = useDataFetch();
  const callRef = useRef(callAPI);
  callRef.current = callAPI;
  const { getLookupValues, getHotTokens } = useConfig();
  const { getToken } = useCookie();
  const { feedData, connect } = useSocket(LIVE_STAGING);

  const algorithms = useMemo(
    () => (getLookupValues('ScreeningAlgorithms') || getLookupValues('Algorithms') || []).map((a) => ({ value: String(a.key), label: a.value && a.value !== a.key ? `${a.value} (${a.key})` : String(a.key) })),
    [],
  );

  const [algoKey, setAlgoKey] = useState('');
  // Symbols: the algorithm's hot tokens (default) or every symbol with a trend row. Like the
  // algorithm, it applies on the next Analyze / Live Screening - not on change.
  const [universe, setUniverse] = useState('hot');
  const universes = useMemo(() => {
    const count = algoKey ? getHotTokens(algoKey).size : null;
    return [
      { value: 'all', label: 'All' },
      { value: 'hot', label: count == null ? 'Hot Tokens' : `Hot Tokens (${count})` },
    ];
  }, [algoKey, getHotTokens]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  // --- Live Screening: a backend job reads Angel quotes every scanInterval and pushes each reading
  // over the WebSocket (staging "liveScreening"). The last reading stays on screen after it stops,
  // until the next Analyze.
  const [liveFeed, setLiveFeed] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);
  useEffect(() => {
    if (feedData) setLiveFeed(feedData);
  }, [feedData]);

  const liveStatus = useCallback(async () => {
    const res = await callRef.current(Config.ITradeAPI.screeningLiveStatus, {});
    return res.status ? res.data : null;
  }, []);

  // Back from the background (phones suspend the page and drop its socket): re-read the live job's
  // state right away rather than waiting for the next pushed reading - only while live data is shown.
  const liveShownRef = useRef(false);
  liveShownRef.current = Boolean(liveFeed?.analysis);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !liveShownRef.current) return;
      liveStatus().then((feed) => {
        if (feed && feed.status !== 'idle') setLiveFeed(feed);
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [liveStatus]);

  // Socket for the live readings; a job may still be running from before this page was opened
  // (or reloaded) - pick it back up.
  useEffect(() => {
    const profileID = getToken()?.profileID;
    if (profileID) connect(profileID);
    liveStatus().then((feed) => {
      if (!feed || feed.status === 'idle') return;
      setLiveFeed(feed);
      if (feed.status === 'running' && feed.algoKey) setAlgoKey(feed.algoKey);
      if (feed.analysis?.universe) setUniverse(feed.analysis.universe);
    });
  }, []);

  const isLive = liveFeed?.status === 'running';
  // What's on screen: the live reading while/after Live Screening, otherwise the Analyze result.
  const liveAnalysis = liveFeed?.analysis ?? null;
  const shown = liveAnalysis ?? analysis;
  const liveView = liveAnalysis != null;
  // The latest refresh failed (temporary - the job retries at the next refresh), including a first
  // refresh that never succeeded.
  const liveStale = Boolean(isLive && liveFeed?.lastAttempt && (!liveFeed.lastUpdated || liveFeed.lastAttempt > liveFeed.lastUpdated));
  const lastLiveTick = liveFeed?.lastUpdated && liveFeed.lastAttempt
    ? (liveFeed.lastAttempt > liveFeed.lastUpdated ? liveFeed.lastAttempt : liveFeed.lastUpdated)
    : liveFeed?.lastUpdated ?? liveFeed?.lastAttempt ?? null;

  useEffect(() => {
    if (!algoKey && algorithms.length) setAlgoKey(algorithms[0].value);
  }, [algoKey, algorithms]);

  // Live Screening is only available during trading hours - the buy engine's own market check, once.
  const [market, setMarket] = useState(UNKNOWN_MARKET);
  useEffect(() => {
    let cancelled = false;
    callRef.current(Config.ITradeAPI.screeningMarketStatus, {}).then((res) => {
      if (cancelled) return;
      if (!res.status) setMarket({ open: false, label: 'Market status unavailable - reload to re-check' });
      else setMarket(res.data?.open ? { open: true, label: 'Market open' } : { open: false, label: 'Market closed - reload to re-check' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const runAnalyze = async (key = algoKey) => {
    if (!key || isLive) return;
    setAnalyzing(true);
    setError(null);
    setLiveFeed(null);
    try {
      const res = await callAPI(Config.ITradeAPI.screeningAnalyze, { algoKey: key, universe });
      if (res.status) setAnalysis(res.data);
      else setError(res.error || 'Analysis failed.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Not reactive: a new algorithm (or Symbols choice) applies on the next Analyze / Live Screening.
  const onAlgoKey = (key) => setAlgoKey(key);

  const onLive = async () => {
    if (!algoKey) return;
    setLiveBusy(true);
    setError(null);
    try {
      const res = await callAPI(Config.ITradeAPI.screeningLiveStart, { algoKey, universe });
      if (res.status) setLiveFeed(res.data);
      else setError(res.error || 'Could not start Live Screening.');
    } finally {
      setLiveBusy(false);
    }
  };

  const onStopLive = async () => {
    setLiveBusy(true);
    try {
      const res = await callAPI(Config.ITradeAPI.screeningLiveStop, {});
      if (res.status && res.data?.status !== 'idle') setLiveFeed((prev) => ({ ...prev, ...res.data }));
      else if (!res.status) setError(res.error || 'Could not stop Live Screening.');
    } finally {
      setLiveBusy(false);
    }
  };

  const rows = useMemo(() => shown?.instruments ?? [], [shown]);
  const groups = useMemo(() => groupByTrend(rows), [rows]);
  const total = useMemo(() => statsOf(rows), [rows]);
  const universeLabel = shown?.universe === 'all' ? 'All symbols' : `Hot tokens (${shown?.hotTokenCount ?? 0})`;

  return (
    <Box sx={{ maxWidth: 1680, mx: 'auto', px: { xs: 0.5, sm: 1.5 }, pb: 6 }}>
      {/* Title */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} spacing={1.5} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h4">Screening</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {liveView
              ? 'Live quotes and trend scores, refreshed every minute - compared with the trend table, plus the Fishing bucket.'
              : "Trend buckets for the selected algorithm, with each symbol's current quote and backtest win rate."}
          </Typography>
        </Box>
        {shown && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {liveView && (
              <Chip
                size="small" icon={<SensorsIcon />} color={isLive ? 'secondary' : 'default'} variant={isLive ? 'filled' : 'outlined'}
                label={`${isLive ? 'Live' : 'Last live reading'} · ${liveFeed?.lastUpdated ? new Date(liveFeed.lastUpdated).toLocaleTimeString() : '—'}`}
              />
            )}
            <Chip size="small" icon={<EventIcon />} label={`${liveView ? 'Table trend' : 'Trend'} as of ${fmtDate(shown.trendDate)}`} variant="outlined" />
            {shown.backtest && (
              <Chip
                size="small" icon={<HistoryIcon />} variant="outlined"
                label={`Win % · ${shown.backtest.duration ?? ''} backtest ${fmtMonthYear(shown.backtest.fromDate)} – ${fmtMonthYear(shown.backtest.toDate)}`}
              />
            )}
          </Stack>
        )}
      </Stack>

      <FilterBar
        algorithms={algorithms}
        universes={universes}
        algoKey={algoKey}
        universe={universe}
        onAlgoKey={onAlgoKey}
        onUniverse={(v) => setUniverse(v === 'all' ? 'all' : 'hot')}
        onAnalyze={() => void runAnalyze()} onLive={() => void onLive()} onStopLive={() => void onStopLive()}
        analyzing={analyzing} market={market} live={isLive} liveBusy={liveBusy}
      />

      {isLive && lastLiveTick && (
        <Paper sx={{ mt: 2, px: { xs: 2, md: 2.5 }, py: 1.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Refresh {liveFeed.scan ?? 0} · {liveAnalysis?.quoted ?? 0}/{liveAnalysis?.instruments.length ?? 0} symbols scored live
              {' · '}{liveFeed.fishing?.length ?? 0} caught
            </Typography>
            <RefreshCountdown lastUpdated={lastLiveTick} intervalMs={liveFeed.scanInterval ?? 60_000} />
          </Stack>
        </Paper>
      )}
      {liveStale && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {liveFeed?.message} - Live Screening keeps running and retries at the next refresh.
        </Alert>
      )}
      {liveFeed && !isLive && liveFeed.status !== 'idle' && liveFeed.message && (
        <Alert severity={liveFeed.status === 'error' ? 'error' : 'info'} sx={{ mt: 2 }}>{liveFeed.message}</Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2.5 }} action={<Button color="inherit" size="small" onClick={() => void runAnalyze()}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2.5 }}>
        {(analyzing || liveBusy) && !shown ? (
          <LoadingState />
        ) : !shown ? (
          !error && <EmptyState />
        ) : (
          <Stack spacing={3} sx={{ opacity: analyzing ? 0.55 : 1, transition: 'opacity .2s' }}>
            {rows.length === 0 ? (
              <Alert severity="info">
                {shown.universe === 'hot'
                  ? `No hot tokens for ${shown.algoName ?? shown.algoKey} yet - sync Hot Tokens in IN.Core's BackTest Screening, or choose Symbols: All.`
                  : 'No symbols to screen - run Configure Trend in IN.Core and Partner first.'}
              </Alert>
            ) : (
              <>
                <SummaryHeader groups={groups} total={total} />

                {liveView && <FishingCard fishing={liveFeed?.fishing ?? []} rows={rows} />}

                <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
                  <TrendPanel side="bullish" groups={groups} live={liveView} />
                  <TrendPanel side="bearish" groups={groups} live={liveView} />
                </Box>

                <NeutralStrip rows={groups.get(0) ?? []} live={liveView} />

                {/* Whole universe */}
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    background: (t) => `linear-gradient(90deg, ${alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.08)}, transparent 70%)`,
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                    <Box>
                      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>Universe · total</Typography>
                      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{shown.algoName ?? shown.algoKey} · {universeLabel}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {total.count} symbols · equal-weight open → {liveView ? 'live price growth today' : 'last price growth on the latest quote'}
                        {total.avgWinRate != null && <> · avg win {total.avgWinRate.toFixed(1)}%</>}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography sx={{ color: 'success.main', fontWeight: 800 }}>▲ {total.advancers}</Typography>
                      <Typography sx={{ color: 'error.main', fontWeight: 800 }}>▼ {total.decliners}</Typography>
                      <GrowthPill value={total.avgGrowth} large />
                    </Stack>
                  </Stack>
                </Paper>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function EmptyState() {
  return (
    <Paper sx={{ py: { xs: 6, md: 9 }, px: 3, textAlign: 'center' }}>
      <Box
        sx={{
          width: 76, height: 76, mx: 'auto', mb: 2.5, borderRadius: '24px', display: 'grid', placeItems: 'center',
          color: 'primary.main',
          background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.18)}, ${alpha(t.palette.secondary.main, 0.18)})`,
          '& svg': { fontSize: 38 },
        }}
      >
        <InsightsIcon />
      </Box>
      <Typography variant="h5">Analyze an algorithm's trend</Typography>
      <Typography variant="body2" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
        Pick an algorithm and press <b>Analyze</b> to see every symbol grouped into bullish and bearish
        buckets (+4 … -4), with its current quote, trend movement and backtest win rate.
      </Typography>
    </Paper>
  );
}

function LoadingState() {
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={128} />)}
      </Box>
      <Skeleton variant="rounded" height={86} />
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' } }}>
        {[0, 1].map((i) => (
          <Stack key={i} spacing={2}>
            {[0, 1, 2].map((j) => <Skeleton key={j} variant="rounded" height={260} />)}
          </Stack>
        ))}
      </Box>
    </Stack>
  );
}
